import React, { useState, useEffect, useRef } from "react";
import { Button, Grid, TextField, LinearProgress, Box, Typography, InputLabel, Select, FormControl, MenuItem, Avatar } from "@material-ui/core";
import { useSnackbar } from "notistack";
import { UserProfile, patchProfile, deleteProfilePicture, putProfilePicture } from "api/user";
import { GridTextField } from "components";
import { DatePicker } from "@material-ui/pickers";
import { SportForm, getSportForms } from "api/courts";
import { Autocomplete } from "@material-ui/lab";
import uI18n from "utils/i18n";
import i18n from "./i18n";
import ChangePasswordDialog from "./ChangePasswordDialog";
import ChangeEmailDialog from "./ChangeEmailDialog";

// prop types
interface UserProfileTabProps {
    userProfile: UserProfile | null,
    onFetchUserProfile: () => void,
    onSignOut: () => void,
}

// gender types
const genderTypes = ["male", "female", "diverse", "none"] as const;

// general club settings tab
const UserProfileTab = ({ userProfile, onFetchUserProfile, onSignOut }: UserProfileTabProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [birthday, setBirthday] = useState("");
    const [firstName, setFirstName] = useState("");
    const [gender, setGender] = useState<typeof genderTypes[number]>("none");
    const [generalSportForms, setGeneralSportForms] = useState<SportForm[]>([]);
    const [isLoading, setLoading] = useState(false);
    const [lastName, setLastName] = useState("");
    const [showChangeEmailDialog, setShowChangeEmailDialog] = useState(false);
    const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
    const [sportForms, setSportForms] = useState<string[]>([]);
    const [statusText, setStatusText] = useState("");
    const inputPicRef = useRef<HTMLInputElement>(null);

    // general api action
    const apiAction = (action: () => void) => async () => {
        setLoading(true);
        try {
            await action();
            onFetchUserProfile(); // refresh
            enqueueSnackbar(i18n.savedSuccessfully, { variant: "success" });
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // remove profile picture action
    const removeProfilePicAction = apiAction(deleteProfilePicture);

    // change profile picture action
    const updateProfilePicAction = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files![0];
        if (file) {
            if (file.size > 3000000) {
                enqueueSnackbar(i18n.fileTooLarge, { variant: "error" });
            } else {
                await apiAction(() => putProfilePicture({
                    profilePicture: file,
                }))();
            }
        }
    };

    // save action
    const saveAction = apiAction(() => patchProfile({
        birthday,
        firstName,
        gender,
        lastName,
        sportForms,
        statusText,
    }));

    // fetch all sport forms
    const fetchGeneralSportForms = async () => { setGeneralSportForms(await getSportForms()); };
    useEffect(() => { fetchGeneralSportForms(); }, []);

    // update fields
    useEffect(() => {
        if (userProfile) {
            setBirthday(userProfile.birthday);
            setFirstName(userProfile.firstName);
            setGender(userProfile.gender);
            setLastName(userProfile.lastName);
            setSportForms(userProfile.sportForms);
            setStatusText(userProfile.statusText);
        }
    }, [userProfile]);

    // render
    const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);
    return (
        <Box style={{ alignItems: "center", display: "flex", flexDirection: "column", padding: "16px" }}>
            <Grid container spacing={2} justifyContent="center">

                {/* personal data */}
                <Grid item xs={12}>
                    <Typography align="center" variant="subtitle2">{i18n.personalData}</Typography>
                </Grid>
                <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                    <Avatar src={userProfile?.profilePictureUrl} style={{ width: "144px", height: "144px" }} />
                </Grid>
                <input accept="image/*" ref={inputPicRef} style={{ display: "none" }} type="file" onChange={updateProfilePicAction} />
                <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                    <Button variant="contained" onClick={() => inputPicRef.current!.click()} disabled={isLoading}>
                        {i18n.change}
                    </Button>
                    <Button variant="contained" onClick={removeProfilePicAction} disabled={isLoading} style={{ marginLeft: "8px" }}>
                        {i18n.remove}
                    </Button>
                </Grid>
                <GridTextField size={12} label={i18n.status} maxLength={255} value={statusText} placeholder={i18n.statusPlaceholder} onChange={setStatusText} />
                <GridTextField size={6} label={i18n.firstName} value={firstName} onChange={setFirstName} />
                <GridTextField size={6} label={i18n.lastName} value={lastName} onChange={setLastName} />
                <Grid item xs={6}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>{i18n.gender}</InputLabel>
                        <Select
                            label={i18n.gender}
                            value={gender}
                            onChange={(e) => setGender(e.target.value as typeof gender)}
                        >
                            {genderTypes.map((t) => (
                                <MenuItem key={t} value={t}>{i18n.genders[t]}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    <DatePicker
                        autoOk
                        format="DD.MM.YYYY"
                        fullWidth
                        inputVariant="outlined"
                        label={i18n.birthday}
                        value={birthday}
                        variant="inline"
                        onChange={(m) => setBirthday(m!.startOf("day").format("YYYY-MM-DD"))}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Autocomplete
                        filterSelectedOptions
                        getOptionDisabled={(option) => !(generalSportForms.find((f) => f.name === option)?.available)}
                        getOptionLabel={(option) => uI18n.sportForms[option] || capitalize(option)}
                        multiple
                        options={generalSportForms.map((f) => f.name).sort()}
                        renderInput={(params) => (
                            <TextField {...params} label={i18n.favoriteSportForms} variant="outlined" />
                        )}
                        value={sportForms}
                        onChange={(e, v) => setSportForms(v)}
                    />
                </Grid>
                {isLoading && (
                    <Grid item xs={12}>
                        <LinearProgress />
                    </Grid>
                )}
                <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                    <Button
                        color="primary"
                        disabled={isLoading}
                        variant="contained"
                        onClick={saveAction}
                    >
                        {i18n.save}
                    </Button>
                </Grid>

                {/* security */}
                <Grid item xs={12} style={{ marginTop: "32px" }}>
                    <Typography align="center" variant="subtitle2">{i18n.security}</Typography>
                </Grid>
                <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                    <Button variant="contained" onClick={() => setShowChangeEmailDialog(true)}>
                        {i18n.changeEmailAddress}
                    </Button>
                </Grid>
                <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                    <Button variant="contained" onClick={() => setShowChangePasswordDialog(true)}>
                        {i18n.changePassword(userProfile?.isAlias)}
                    </Button>
                </Grid>
                <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                    <Button variant="contained" onClick={onSignOut}>
                        {i18n.signOut}
                    </Button>
                </Grid>

            </Grid>

            {/* dialogs */}
            <ChangePasswordDialog
                isAlias={userProfile?.isAlias || false}
                show={showChangePasswordDialog}
                onClose={() => setShowChangePasswordDialog(false)}
            />
            <ChangeEmailDialog
                isAlias={userProfile?.isAlias || false}
                show={showChangeEmailDialog}
                onClose={() => setShowChangeEmailDialog(false)}
            />
        </Box>
    );
};

export default UserProfileTab;
